import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { TeamArt } from '@/components/illustrations';
import { ExportCsvButton } from '@/components/export-csv-button';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { useMyTeam } from '@/lib/analytics-api';
import { assessmentStatusVariant, PILL_CLASS } from '@/lib/badges';

/** Your direct reports and where each one is in the cycle. */
export function TeamPage() {
  const { data: team, isPending } = useMyTeam();

  const rows = team ?? [];
  const withoutGoals = rows.filter((r) => !r.goalsAccepted).length;
  const overdue = rows.filter((r) => r.overdue).length;
  const rated = rows.filter((r) => r.rating !== null);
  const average =
    rated.length === 0
      ? null
      : Math.round((rated.reduce((a, b) => a + (b.rating ?? 0), 0) / rated.length) * 100) / 100;

  if (isPending) return <PageContainer><Skeleton className="h-64 w-full" /></PageContainer>;

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Direct reports" value={rows.length} caption="you review their assessments" />
        <StatTile
          label="Goals live"
          value={`${rows.length - withoutGoals}/${rows.length || 0}`}
          tone={withoutGoals > 0 ? 'warning' : 'good'}
          caption={withoutGoals === 0 ? 'everyone is set up' : `${withoutGoals} still unpublished`}
        />
        <StatTile
          label="Overdue reviews"
          value={overdue}
          tone={overdue > 0 ? 'critical' : 'good'}
          caption={overdue > 0 ? 'past your 3-day deadline' : 'nothing overdue'}
        />
        <StatTile
          label="Team average"
          value={average ?? '—'}
          caption={average === null ? 'no final ratings yet' : `across ${rated.length} rated`}
        />
      </div>

      <div className="flex justify-end">
        <ExportCsvButton
          filename="my-team"
          label="Export team report"
          headers={['Employee', 'Goals live', 'Assessment status', 'Overdue', 'Rating']}
          rows={rows.map((r) => [
            r.name,
            r.goalsAccepted ? 'Yes' : 'No',
            r.assessmentStatus ?? '',
            r.overdue ? 'Yes' : 'No',
            r.rating,
          ])}
        />
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {rows.length === 0 ? (
            <EmptyState
              art={TeamArt}
              title="No direct reports"
              body="HR assigns reporting lines. If this looks wrong, ask them to check yours."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Goals</TableHead>
                    <TableHead>Assessment</TableHead>
                    <TableHead className="text-right">Rating</TableHead>
                    <TableHead className="w-[180px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.employeeId}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge
                          variant={r.goalsAccepted ? 'soft-success' : 'soft-warning'}
                          className={PILL_CLASS}
                        >
                          {r.goalsAccepted ? 'Live' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.assessmentStatus ? (
                          <Badge
                            variant={
                              r.overdue
                                ? 'soft-destructive'
                                : assessmentStatusVariant(r.assessmentStatus as never)
                            }
                            className={PILL_CLASS}
                          >
                            {r.assessmentStatus.replace(/_/g, ' ').toLowerCase()}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="pms-num text-right font-semibold">
                        {r.rating ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button hint={"Open this person's goals"} asChild size="sm" variant="ghost">
                          <Link to={`/team/${r.employeeId}/goals`}>Goals</Link>
                        </Button>
                        <Button hint={"Open this person's assessment"} asChild size="sm" variant="ghost">
                          <Link to={`/team/${r.employeeId}/assessment`}>Assessment</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
